import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as date from 'date-fns';
import { useBillingApi } from 'api';
import * as analytics from 'lib/analytics';
import { useBrandStore, useUserStore } from 'stores';
import TrialEndedBannerView from './TrialEnded.Banner.View';
import TrialEndedPillView from './TrialEnded.Pill.View';
import { BillingPlanSubscription } from 'types/billing';
import { debounce } from 'lodash';
import { BrandStatus } from 'types/stores/brand-store';
import { getDaysRemaining } from 'utils/getDaysRemaining';
import { BillingStatus1 } from '@innovationdepartment/proxima-sdk-axios';

const debouncer = debounce((cb) => cb(), 500);

const TrialEndedContainer = () => {
  const navigate = useNavigate();
  const { brand } = useBrandStore();
  const location = useLocation();
  const { user } = useUserStore();
  const { getBrandSubscription } = useBillingApi();
  const [subscription, setSubscription] = useState<BillingPlanSubscription>();

  const ROUTES_TO_INCLUDE = [
    `/brand/${brand?.brandId}/audiences`,
    `/brand/${brand?.brandId}/integrations`,
  ];

  const fetchSubscription = async () => {
    if (brand?.brandId) {
      const response = await getBrandSubscription(brand?.brandId);

      if (response.error) return;

      setSubscription(response.data);
    }
  };

  const onOpenManageSubscriptionModal = () => {
    analytics.trackActionEvent({
      action: 'Clicked',
      userId: user!.userId,
      brandId: brand?.brandId,
      location: 'TrialEndedContainerChoosePlan',
      element: 'ChoosePlanButton',
    });

    navigate(`/brand/${brand?.brandId}/settings/billing`);
  };

  useEffect(() => {
    debouncer(fetchSubscription);
  }, [brand]);
  // Do not render if no brand, subscription, or invalid route
  if (!brand || !subscription) return null;

  // do not show if brand is managed
  if (brand.isManaged) return null;

  const currentPageLocation = location.pathname;
  const shouldShowBanner = ROUTES_TO_INCLUDE.includes(currentPageLocation);
  const brandIsLocked = brand.status === BrandStatus.Locked;

  if (!shouldShowBanner || brandIsLocked) return null;

  const { status, trialEnd } = subscription;
  const trialEndDate = new Date(trialEnd);
  const trialExpired = date.isAfter(new Date(), trialEndDate);
  const daysRemaining = getDaysRemaining(trialEndDate);

  const isTrialStatus = status === BillingStatus1.Trial;

  if (status === BillingStatus1.Cancelled) {
    // Subscription was cancelled, show banner
    return <TrialEndedBannerView onOpenManageSubscriptionModal={onOpenManageSubscriptionModal} />;
  }

  if (isTrialStatus) {
    if (trialExpired) {
      // Trial period is over, show banner
      return <TrialEndedBannerView onOpenManageSubscriptionModal={onOpenManageSubscriptionModal} />;
    }

    if (daysRemaining <= 30 && daysRemaining > 0) {
      // Trial is about to end, show pill
      return (
        <TrialEndedPillView
          daysRemaining={daysRemaining}
          onOpenManageSubscriptionModal={onOpenManageSubscriptionModal}
        />
      );
    }
  }

  return null;
};

export default TrialEndedContainer;
